import { useEffect, useMemo, useRef, useState } from "react";
import { Auth } from "./auth";
import { api, uploadImage } from "./api";
import type { DayBundle } from "./types";
import { SignIn } from "./SignIn";
import { DayCard } from "./DayCard";

const dubaiToday = (() => {
  const now = new Date();
  const dubai = new Date(now.getTime() + (4 * 60 - now.getTimezoneOffset()) * 60_000);
  return dubai.toISOString().slice(0, 10);
})();

export function App() {
  const [signedIn, setSignedIn] = useState(!!Auth.loadIdToken());
  const [days, setDays] = useState<DayBundle[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    try {
      setBusy(true);
      const r = await api.days("2026-05-15", "2026-11-01");
      const sorted = (r.days as DayBundle[]).sort((a, b) =>
        a.plan.dateISO.localeCompare(b.plan.dateISO),
      );
      setDays(sorted);
      setError(null);
    } catch (e: any) {
      setError(String(e?.message ?? e));
      if (String(e).includes("Not signed in") || String(e).includes("401")) {
        Auth.signOut();
        setSignedIn(false);
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (signedIn) void reload();
  }, [signedIn]);

  // Auto-select today (or first day if today's not in range) once data lands.
  useEffect(() => {
    if (selectedDate || days.length === 0) return;
    const today = days.find((d) => d.plan.dateISO === dubaiToday);
    setSelectedDate(today?.plan.dateISO ?? days[0]?.plan.dateISO ?? null);
  }, [days, selectedDate]);

  const weeks = useMemo(() => {
    // Map<week, DayBundle[]>
    const m = new Map<number, DayBundle[]>();
    for (const d of days) {
      const w = d.plan.week;
      if (!m.has(w)) m.set(w, []);
      m.get(w)!.push(d);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [days]);

  const selectedBundleForWeek = useMemo(
    () => days.find((d) => d.plan.dateISO === selectedDate) ?? null,
    [days, selectedDate],
  );
  const selectedWeek = selectedBundleForWeek?.plan.week ?? null;
  const weekDays = useMemo(
    () => (selectedWeek != null ? weeks.find(([w]) => w === selectedWeek)?.[1] ?? [] : []),
    [weeks, selectedWeek],
  );

  function shiftWeek(delta: number) {
    if (selectedWeek == null) return;
    const i = weeks.findIndex(([w]) => w === selectedWeek);
    const next = weeks[i + delta];
    if (!next) return;
    const wDays = next[1];
    const today = wDays.find((d) => d.plan.dateISO === dubaiToday);
    const target = today ?? wDays[0];
    if (target) setSelectedDate(target.plan.dateISO);
  }

  function selectWeek(week: number) {
    const wDays = weeks.find(([w]) => w === week)?.[1] ?? [];
    if (wDays.length === 0) return;
    const today = wDays.find((d) => d.plan.dateISO === dubaiToday);
    const target = today ?? wDays[0];
    if (target) setSelectedDate(target.plan.dateISO);
  }

  if (!signedIn) {
    return <SignIn onSignedIn={() => setSignedIn(true)} />;
  }

  return (
    <>
      <div className="toolbar">
        <h1>Cut · 2026</h1>
        <div className="actions">
          <button className="icon-btn" title="Sync" onClick={reload} disabled={busy}>↻</button>
          <button className="icon-btn" title="Weigh-in" onClick={openWeighIn}>⚖</button>
          <button className="icon-btn" title="Upload schedule photo" onClick={() => openPhotoPicker("schedule")}>📷</button>
          <button className="icon-btn" title="Sign out" onClick={() => { Auth.signOut(); setSignedIn(false); }}>⎋</button>
        </div>
      </div>

      <WeekStrip
        weeks={weeks.map(([w, ds]) => ({
          week: w,
          dates: ds.map((d) => d.plan.dateISO),
          phase: ds[0]?.plan.phase ?? "base",
        }))}
        selectedWeek={selectedWeek}
        onSelect={selectWeek}
      />

      <div className="week-nav">
        <button className="arrow-btn" onClick={() => shiftWeek(-1)} title="Previous week">‹</button>
        <div className="week-nav-label">
          {selectedWeek != null ? `Week ${selectedWeek}` : "—"}
        </div>
        <button className="arrow-btn" onClick={() => shiftWeek(1)} title="Next week">›</button>
      </div>

      {error && <div style={{ color: "var(--red)", padding: "12px 24px", fontSize: 12 }}>{error}</div>}

      <div className="week-grid">
        {weekDays.map((b) => (
          <div
            key={b.plan.dateISO}
            className={`week-grid-col ${b.plan.dateISO === dubaiToday ? "is-today" : ""}`}
          >
            <DayCard bundle={b} initiallyExpanded={true} onMutated={reload} />
          </div>
        ))}
        {weekDays.length === 0 && !busy && (
          <div style={{ color: "var(--muted)", padding: 24, textAlign: "center", gridColumn: "1 / -1" }}>
            No days for this week.
          </div>
        )}
      </div>
    </>
  );

  function openWeighIn() {
    const v = prompt("Weight (kg)?");
    if (!v) return;
    const kg = Number(v);
    if (!Number.isFinite(kg)) return;
    api.postMetrics([{
      dateISO: dubaiToday, kind: "bodyMass", value: kg, unit: "kg",
      source: "manual", takenAt: new Date().toISOString(),
    }]).then(reload).catch((e) => alert(String(e)));
  }

  function openPhotoPicker(kind: "schedule" | "other") {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const note = prompt(`Note for this ${kind} photo (optional):`) ?? undefined;
      try {
        await uploadImage(file, { dateISO: dubaiToday, kind, note });
        alert("Uploaded — Claude will review it next time you ask.");
      } catch (e) {
        alert(`Upload failed: ${e}`);
      }
    };
    input.click();
  }
}

// ─── Week strip ────────────────────────────────────────────────────────────

function WeekStrip({
  weeks,
  selectedWeek,
  onSelect,
}: {
  weeks: { week: number; dates: string[]; phase: string }[];
  selectedWeek: number | null;
  onSelect: (w: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // Auto-scroll the active week into view when selectedWeek changes.
  useEffect(() => {
    const node = ref.current?.querySelector(`[data-week="${selectedWeek}"]`) as HTMLElement | null;
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [selectedWeek]);

  function shortRange(dates: string[]) {
    const first = dates[0];
    const last = dates[dates.length - 1];
    if (!first || !last) return "";
    return `${first.slice(8, 10)}/${first.slice(5, 7)} – ${last.slice(8, 10)}/${last.slice(5, 7)}`;
  }

  return (
    <div className="week-strip" ref={ref}>
      {weeks.map(({ week, dates, phase }) => (
        <button
          key={week}
          data-week={week}
          className={`week-pill ${week === selectedWeek ? "active" : ""}`}
          onClick={() => onSelect(week)}
          title={`${phase}`}
        >
          <span className="week-num">Wk {week}</span>
          <span className="week-range">{shortRange(dates)}</span>
        </button>
      ))}
    </div>
  );
}

